/**
 * IDAReviewPanel.tsx
 *
 * IDA Review voting interface. Displays voting round with 5 voters,
 * allows casting votes, and closes the round when majority exists.
 *
 * Props:
 * - projectId: number — project to fetch and manage voting for
 *
 * API Interactions:
 * - GET /api/v2/projects/:id/vote-rounds — fetch all rounds
 * - POST /api/v2/projects/:id/vote-rounds — create new IDA_REVIEW round
 * - POST /api/v2/vote-rounds/:roundId/votes — cast a vote
 * - PATCH /api/v2/vote-rounds/:roundId/close — close a round
 */

import { useEffect, useState } from 'react'
import { authFetchJson } from '../../lib/authFetch'
import type { VoteRound, LifecycleProjectVote, VoteValue } from '../../types'

interface IDAReviewPanelProps {
  projectId: number
}

interface VoteRoundWithVotes {
  id: number
  project_id: number
  round_type: string
  status: string
  created_by: number | null
  created_at: string
  closed_at: string | null
  result: string | null
  votes?: LifecycleProjectVote[]
}

interface ApiRoundsResponse {
  rounds: VoteRoundWithVotes[]
}

interface ApiRoundCreateResponse {
  round: VoteRoundWithVotes
  message?: string
}

interface ApiVoteResponse {
  vote: LifecycleProjectVote
  message?: string
}

interface ApiCloseRoundResponse {
  round: VoteRoundWithVotes
  result?: string
  message?: string
}

// Default voter list for IDA Review (4 VAs + Director)
const DEFAULT_VOTERS = [
  { name: 'Venture Architect 1', role: 'VA · Lead' },
  { name: 'Venture Architect 2', role: 'VA' },
  { name: 'Venture Architect 3', role: 'VA' },
  { name: 'Venture Architect 4', role: 'VA' },
  { name: 'Director of R&D Collaboration', role: 'Director' },
]

// Vote badge color map
const VOTE_BADGE_COLORS: Record<VoteValue, string> = {
  'PROMOTE': 'bg-[#10B981] text-white',
  'HOLD': 'bg-[#F59E0B] text-white',
  'REJECT': 'bg-[#E61B25] text-white',
}

const PENDING_BADGE_COLOR = 'bg-[#4a4a6a] text-white'

export function IDAReviewPanel({ projectId }: IDAReviewPanelProps) {
  const [rounds, setRounds] = useState<VoteRoundWithVotes[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [castingVote, setCastingVote] = useState<{
    voterName: string
    vote: VoteValue | ''
    comment: string
  } | null>(null)
  const [savingVote, setSavingVote] = useState(false)

  // Fetch voting rounds on mount
  useEffect(() => {
    const fetchRounds = async () => {
      try {
        setLoading(true)
        const data = await authFetchJson<ApiRoundsResponse>(
          `/api/v2/projects/${projectId}/vote-rounds`
        )
        setRounds(data.rounds)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch voting rounds')
      } finally {
        setLoading(false)
      }
    }

    fetchRounds()
  }, [projectId])

  // Get IDA_REVIEW round (most recent OPEN one, or most recent overall)
  const idaRound = rounds.find(r => r.round_type === 'IDA_REVIEW')

  const createNewRound = async () => {
    try {
      const data = await authFetchJson<ApiRoundCreateResponse>(
        `/api/v2/projects/${projectId}/vote-rounds`,
        {
          method: 'POST',
          json: { round_type: 'IDA_REVIEW' },
        }
      )
      setRounds(prev => [data.round, ...prev])
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create voting round')
    }
  }

  const castVote = async (voterName: string, vote: VoteValue, comment: string) => {
    if (!idaRound) return

    try {
      setSavingVote(true)
      const data = await authFetchJson<ApiVoteResponse>(
        `/api/v2/vote-rounds/${idaRound.id}/votes`,
        {
          method: 'POST',
          json: {
            voter_name: voterName,
            voter_role: 'Venture Architect',
            vote,
            comment: comment || undefined,
          },
        }
      )

      // Update the round with the new vote
      setRounds(prev =>
        prev.map(r =>
          r.id === idaRound.id
            ? { ...r, votes: [...(r.votes || []), data.vote] }
            : r
        )
      )
      setCastingVote(null)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cast vote')
    } finally {
      setSavingVote(false)
    }
  }

  const closeRound = async () => {
    if (!idaRound) return

    try {
      setSavingVote(true)
      const data = await authFetchJson<ApiCloseRoundResponse>(
        `/api/v2/vote-rounds/${idaRound.id}/close`,
        { method: 'PATCH' }
      )

      // Update the round status and result
      setRounds(prev =>
        prev.map(r =>
          r.id === idaRound.id
            ? {
              ...data.round,
              votes: data.round.votes || r.votes || []
            }
            : r
        )
      )
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close voting round')
    } finally {
      setSavingVote(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-4 bg-[#2a2a44] rounded w-1/3 animate-pulse" />
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-[#2a2a44] rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (!idaRound) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-4">
        <p className="text-[#a8a8c0]">No IDA Review voting round yet</p>
        <button
          onClick={createNewRound}
          className="px-4 py-2 bg-[#E61B25] text-white rounded-lg text-sm font-medium hover:bg-[#d41620] transition-colors"
        >
          Open IDA Review
        </button>
      </div>
    )
  }

  // Build voter list with votes
  const votes = idaRound?.votes || []
  const votesByName: Record<string, LifecycleProjectVote> = Object.fromEntries(
    votes.map(v => [v.voter_name, v])
  )

  const voters = DEFAULT_VOTERS.map(voter => ({
    ...voter,
    vote: votesByName[voter.name] || null,
  }))

  // Calculate tally
  const promoteCount = votes.filter(v => (v.vote as string) === 'PROMOTE').length
  const holdCount = votes.filter(v => (v.vote as string) === 'HOLD').length
  const rejectCount = votes.filter(v => (v.vote as string) === 'REJECT').length

  // Determine if we have majority for PROMOTE
  const totalVotes = votes.length
  const hasMajority = totalVotes > 0 && promoteCount > totalVotes / 2

  // Get status badge color
  const statusBg = idaRound.status === 'OPEN' ? 'bg-[#10B981]' : 'bg-[#4a4a6a]'
  const statusText = idaRound.status === 'OPEN' ? 'OPEN' : 'CLOSED'

  return (
    <div className="space-y-6">
      {error && (
        <div className="px-4 py-3 bg-[#E61B25]/10 border border-[#E61B25]/30 rounded-lg text-[#E61B25] text-sm">
          {error}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[16px] font-semibold text-[#e8e8f0]">Idea Review Meeting</h3>
          <p className="text-[13px] text-[#a8a8c0] mt-1">
            Opened {idaRound.created_at ? new Date(idaRound.created_at).toLocaleDateString() : 'recently'}
          </p>
        </div>
        <div className={`${statusBg} text-white text-[11px] font-semibold px-3 py-1 rounded-full`}>
          {statusText}
        </div>
      </div>

      {/* Voter Cards */}
      <div className="space-y-3">
        {voters.map(voter => {
          const hasVoted = !!voter.vote
          const initials = voter.name.split(' ').map(w => w[0]).join('').toUpperCase()

          return (
            <div
              key={voter.name}
              onClick={() => {
                if (idaRound.status === 'OPEN' && !hasVoted) {
                  setCastingVote({
                    voterName: voter.name,
                    vote: '',
                    comment: '',
                  })
                }
              }}
              className={`
                bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4
                flex items-start gap-4
                transition-colors
                ${idaRound.status === 'OPEN' && !hasVoted ? 'cursor-pointer hover:border-[#3a3a54]' : ''}
              `}
            >
              {/* Avatar */}
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-[12px] font-semibold">{initials}</span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-medium text-[#e8e8f0]">{voter.name}</span>
                  <span className="text-[11px] text-[#a8a8c0]">{voter.role}</span>
                </div>

                {/* Comment */}
                {voter.vote?.comment && (
                  <p className="text-[12px] text-[#a8a8c0] italic mt-2">{voter.vote.comment}</p>
                )}
              </div>

              {/* Vote Badge */}
              <div className="flex-shrink-0">
                {voter.vote ? (
                  <div className={`px-3 py-1 rounded-full text-[11px] font-semibold ${VOTE_BADGE_COLORS[voter.vote.vote as VoteValue]}`}>
                    {voter.vote.vote}
                  </div>
                ) : (
                  <div className={`px-3 py-1 rounded-full text-[11px] font-semibold ${PENDING_BADGE_COLOR}`}>
                    PENDING
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Vote Dialog */}
      {castingVote && idaRound.status === 'OPEN' && (
        <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-4 space-y-4">
          <div>
            <label className="text-[12px] font-medium text-[#a8a8c0] block mb-2">
              Vote
            </label>
            <div className="flex gap-2">
              {(['PROMOTE', 'HOLD', 'REJECT'] as VoteValue[]).map(option => (
                <button
                  key={option}
                  onClick={() => setCastingVote({ ...castingVote, vote: option })}
                  className={`
                    flex-1 px-3 py-2 rounded-lg text-[12px] font-medium transition-colors
                    ${castingVote.vote === option
                      ? `${VOTE_BADGE_COLORS[option]} border border-transparent`
                      : 'bg-[#2a2a44] text-[#a8a8c0] border border-[#3a3a54] hover:border-[#4a4a6a]'
                    }
                  `}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[12px] font-medium text-[#a8a8c0] block mb-2">
              Comment (optional)
            </label>
            <textarea
              value={castingVote.comment}
              onChange={e => setCastingVote({ ...castingVote, comment: e.target.value })}
              placeholder="Add a comment..."
              className="w-full px-3 py-2 bg-[#12122a] border border-[#2a2a44] rounded-lg text-[13px] text-[#e8e8f0] placeholder-[#4a4a6a] focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50 resize-none"
              rows={3}
            />
          </div>

          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setCastingVote(null)}
              disabled={savingVote}
              className="px-3 py-2 border border-[#2a2a44] rounded-lg text-[12px] font-medium text-[#a8a8c0] hover:border-[#3a3a54] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (castingVote.vote) {
                  castVote(castingVote.voterName, castingVote.vote, castingVote.comment)
                }
              }}
              disabled={!castingVote.vote || savingVote}
              className="px-4 py-2 bg-[#E61B25] text-white rounded-lg text-[12px] font-medium hover:bg-[#d41620] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingVote ? 'Submitting...' : 'Submit Vote'}
            </button>
          </div>
        </div>
      )}

      {/* Result Bar */}
      {totalVotes > 0 && (
        <div className="bg-[#2a2a44] rounded-lg p-4 space-y-3">
          <div className="text-[13px] text-[#a8a8c0]">
            <span className="font-medium text-[#10B981]">{promoteCount}</span> Promote
            {' '}· <span className="font-medium text-[#F59E0B]">{holdCount}</span> Hold
            {' '}· <span className="font-medium text-[#E61B25]">{rejectCount}</span> Reject
          </div>

          {idaRound.status === 'CLOSED' && idaRound.result && (
            <div className="text-[12px] font-medium">
              Result:{' '}
              <span className={`
                ${idaRound.result === 'PROMOTE' ? 'text-[#10B981]' : ''}
                ${idaRound.result === 'HOLD' ? 'text-[#F59E0B]' : ''}
                ${idaRound.result === 'REJECT' ? 'text-[#E61B25]' : ''}
              `}>
                {idaRound.result}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Action Button */}
      {idaRound.status === 'OPEN' && hasMajority && (
        <button
          onClick={closeRound}
          disabled={savingVote}
          className="w-full px-4 py-3 bg-[#10B981] text-white rounded-lg font-medium hover:bg-[#0a9970] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {savingVote ? 'Closing...' : 'Close Voting & Promote to Extra Mile →'}
        </button>
      )}
    </div>
  )
}

export default IDAReviewPanel
